import React, { useState } from 'react';
import { useFormSubmit } from '../../../hooks/useFormSubmit';
import { z } from 'zod';
import { PHONE_DISPLAY } from '../../../lib/businessInfo';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  message: z.string().min(10, 'Message must be at least 10 characters')
});

export default function AstoriaContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const { handleSubmit, isSubmitting, submitStatus } = useFormSubmit({
    formId: 'astoria-inquiry-form',
    submitUrl: 'https://readdy.ai/api/form/d6lfncqqoe30lj0v7v50',
    schema: formSchema,
    // Escape the apostrophe or use double quotes to avoid syntax error
    successMessage:
      "Thanks for reaching out! We'll get back to you within 24 hours.",
    errorMessage:
      `Failed to submit inquiry. Please try again or call us at ${PHONE_DISPLAY}.`,
    toastOptions: { duration: 5000 }
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const success = await handleSubmit(formData);
      if (success) {
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (err) {
      // Optional: Log or handle unexpected errors
      console.error('Form submission error:', err);
    }
  };

  return (
    <section id="contact-form" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Get in Touch — Astoria Buyers
          </h2>
          <p className="text-lg text-gray-600">
            Questions about a specific vehicle? Want to schedule a delivery to
            Astoria? Drop us a message.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-readdy-form
          id="astoria-inquiry-form"
          className="bg-gray-50 p-8 rounded-lg border border-gray-200"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="phone"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              placeholder="(503) 555-0123"
            />
          </div>

          <div className="mb-6">
            <label
              htmlFor="message"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              required
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-none"
              placeholder="Tell us what you're looking for or ask any questions..."
            />
            <div className="text-sm text-gray-500 mt-1 text-right">
              {formData.message.length}/500 characters
            </div>
          </div>

          {submitStatus && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                submitStatus.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <i
                  className={`${
                    submitStatus.type === 'success'
                      ? 'ri-check-line'
                      : 'ri-error-warning-line'
                  } text-xl`}
                ></i>
                <span>{submitStatus.message}</span>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Sending...
              </span>
            ) : (
              'Send Message'
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
